import { HttpErrorResponse } from "@angular/common/http";
import { Component, OnDestroy, OnInit } from "@angular/core";
import { FormArray, FormGroup } from "@angular/forms";
import { Store } from "@ngrx/store";
import { Observable, ReplaySubject, takeUntil } from "rxjs";
import { SedsModalService } from "@serasa/shared/seds";
import { SiteSection } from "@serasa/shared/enums/site-section.enum";
import { AnalyticsService } from "@serasa/shared/services/analytics.service";
import { PmeLogonsCount } from "../../../recertification/interfaces/user-me.interface";
import {
  CloneLogon,
  CloneLogonResult,
} from "../../interfaces/clone-logon.interface";
import { CloneableLogonSearch } from "../../interfaces/cloneable-logon-search.interface";
import { Logon } from "../../interfaces/logon.interface";
import {
  addClonedUser,
  cleanClonedUser,
  clearCloneLogonSearch,
  closeSideNav,
  loadCloneableLogonSearchData,
  loadUserMeInfo,
} from "../../state/actions/user.actions";
import {
  selectCloneableLogonSearch,
  selectClonedLogon,
  selectLogon,
  selectUserMeInfo,
} from "../../state/selectors/user.selectors";
import { UserAddMessagesHelper } from "../user-add/helpers/user-add-messages.helper";
import {
  IUserAddSearchMessagesHelper,
  UserAddSearchMessagesHelper,
} from "../user-add/helpers/user-add-search-messages.helper";
import { UserAddSuccessModalComponent } from "../../../dashboard/components/user-add-success-modal/user-add-success-modal.component";
import {
  createEmailIdEntry,
  EmailIdEntryForm,
} from "../user-add/components/email-id-list/email-id-list.component";

type SelectedLogonViewModel = Partial<Logon> & {
  error?: HttpErrorResponse | null;
};

export enum ViewProductsStep {
  PRODUCTS = 0,
  FORM = 1,
}

@Component({
  selector: "pl-user-view-products",
  templateUrl: "./user-view-products.component.html",
  styleUrls: ["./user-view-products.component.scss"],
  standalone: false,
})
export class UserViewProductsComponent implements OnInit, OnDestroy {
  public cloneableLogon$: Observable<CloneableLogonSearch | undefined>;
  public clonedLogon$: Observable<CloneLogon | undefined>;
  public selectedLogon$: Observable<SelectedLogonViewModel | undefined>;
  public userMeInfo$ = this.store.select(selectUserMeInfo);
  private destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);

  public cloneableLogonData: CloneableLogonSearch | undefined;
  public selectedLogonData: SelectedLogonViewModel | undefined;
  public isLoading = true;
  public createLogonLoading = false;
  public hasCloneError = false;
  public hasSearchError = false;
  public searchErrorMessage: IUserAddSearchMessagesHelper | undefined;
  public pageName?: string;
  public errorMessage: IUserAddSearchMessagesHelper | undefined;
  public failedEmails: string[] = [];
  public pmeLogonsCount: PmeLogonsCount | undefined;
  public pmeLimitsUnavailable = false;

  // Step control
  public currentStep: ViewProductsStep = ViewProductsStep.PRODUCTS;
  public readonly ViewProductsStep = ViewProductsStep;

  public entriesForm = new FormArray<FormGroup<EmailIdEntryForm>>([
    createEmailIdEntry(true),
  ]);

  constructor(
    private readonly store: Store,
    private readonly _analyticsService: AnalyticsService,
    private readonly _dialog: SedsModalService,
  ) {
    this.cloneableLogon$ = this.store.select(selectCloneableLogonSearch);
    this.clonedLogon$ = this.store.select(selectClonedLogon);
    this.selectedLogon$ = this.store.select(selectLogon);
  }

  public ngOnInit(): void {
    this.pageName = `${SiteSection.CLONAR_USUARIO}:VisualizarProdutos`;
    this._analyticsService.doPageLoad(this.pageName);

    this.store.dispatch(clearCloneLogonSearch());
    this.store.dispatch(cleanClonedUser());
    this.store.dispatch(loadUserMeInfo());

    this.selectedLogon$.pipe(takeUntil(this.destroyed$)).subscribe((logon) => {
      this.selectedLogonData = logon;
      if (logon?.logon) {
        this.store.dispatch(
          loadCloneableLogonSearchData({ logon: logon.logon }),
        );
      } else if (logon?.error) {
        this.isLoading = false;
        this.hasSearchError = true;
        this.searchErrorMessage = new UserAddSearchMessagesHelper(
          logon.error,
        ).error;
      }
    });

    this.cloneableLogon$
      .pipe(takeUntil(this.destroyed$))
      .subscribe((logonSearchData) => {
        if (logonSearchData?.logon || logonSearchData?.error) {
          this.isLoading = false;
        }
        if (logonSearchData?.error) {
          this.hasSearchError = true;
          this.searchErrorMessage = new UserAddSearchMessagesHelper(
            logonSearchData.error,
          ).error;
        } else {
          this.hasSearchError = false;
          this.searchErrorMessage = undefined;
        }
        this.cloneableLogonData = logonSearchData;
      });

    this.userMeInfo$.pipe(takeUntil(this.destroyed$)).subscribe((userMe) => {
      this.pmeLogonsCount = userMe?.pmeLogonsCount;
      this.pmeLimitsUnavailable =
        userMe?.channel?.channelDescription === "PME" && !userMe.pmeLogonsCount;
    });

    this.clonedLogon$
      .pipe(takeUntil(this.destroyed$))
      .subscribe((logonData) => {
        this.createLogonLoading = false;
        this.failedEmails = logonData?.failedEmails ?? [];

        if (logonData?.logon?.length) {
          this.store.dispatch(closeSideNav());
          this._openSuccessModal(logonData.successResults ?? []);
        }

        if (logonData?.error) {
          this.hasCloneError = true;
          this.errorMessage = new UserAddMessagesHelper(logonData.error).error;
        } else {
          this.hasCloneError = false;
        }
      });
  }

  public ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
    this.store.dispatch(clearCloneLogonSearch());
    this.store.dispatch(cleanClonedUser());
  }

  // ==================== Step Navigation ====================

  public goToStep(step: ViewProductsStep): void {
    this.currentStep = step;
    this.updatePageName();
  }

  public goBack(): void {
    if (this.currentStep > ViewProductsStep.PRODUCTS) {
      this.currentStep--;
      this.updatePageName();
    }
  }

  public canGoBack(): boolean {
    return this.currentStep > ViewProductsStep.PRODUCTS;
  }

  private updatePageName(): void {
    switch (this.currentStep) {
      case ViewProductsStep.PRODUCTS:
        this.pageName = `${SiteSection.CLONAR_USUARIO}:VisualizarProdutos`;
        break;
      case ViewProductsStep.FORM:
        this.pageName = `${SiteSection.CLONAR_USUARIO}:CriarUsuario`;
        break;
    }
    this._analyticsService.doPageLoad(this.pageName);
  }

  // ==================== Header Title ====================

  public getHeaderTitle(): string {
    switch (this.currentStep) {
      case ViewProductsStep.PRODUCTS:
        return "Visualizar produtos";
      case ViewProductsStep.FORM:
        return "Adicione quem vai receber o acesso";
      default:
        return "Visualizar produtos";
    }
  }

  // ==================== Description ====================

  public getDescription(): string {
    const name =
      this.selectedLogonData?.fullname || this.selectedLogonData?.logon || "-";
    const email =
      this.selectedLogonData?.email ||
      this.selectedLogonData?.alternativeEmail?.email ||
      "-";
    const logon = this.selectedLogonData?.logon || "-";
    return `Os produtos e serviços de autoatendimento habilitados para ${name} (${email} - ${logon}).`;
  }

  // ==================== Actions ====================

  public closeSideNav(): void {
    this.store.dispatch(closeSideNav());
  }

  public continueToForm(): void {
    this.store.dispatch(loadUserMeInfo());
    this.goToStep(ViewProductsStep.FORM);
  }

  public isPmeLimitReached(): boolean {
    if (!this.pmeLogonsCount) {
      return false;
    }

    return (
      this.pmeLogonsCount.limitReached ||
      this.pmeLogonsCount.remainingCount <= 0
    );
  }

  public addUser(): void {
    if (this.isPmeLimitReached()) {
      this.hasCloneError = true;
      this.errorMessage = {
        title: "Limite de acessos atingido",
        description:
          "Revise os acessos existentes e exclua os que não forem necessários para criar novos usuários a partir deste usuário.",
      };
      return;
    }

    const entries = this.entriesForm.controls
      .map((group) => ({
        email: group.controls.email.value.trim(),
        name: group.controls.accountName.value.trim(),
      }))
      .filter((entry) => !!entry.email);

    if (entries.length === 0) {
      this.hasCloneError = true;
      this.errorMessage = {
        title: "E-mail não informado",
        description:
          "Insira pelo menos um endereço de e-mail para concluir o cadastro.",
      };
      this.entriesForm.markAllAsTouched();
      return;
    }

    if (this.hasInvalidEntries()) {
      this.entriesForm.markAllAsTouched();
      return;
    }

    this.createLogonLoading = true;
    this.hasCloneError = false;
    this.store.dispatch(
      addClonedUser({
        payload: {
          entries: entries.map((entry) => ({
            email: entry.email,
            name: entry.name || undefined,
          })),
          logon: this.selectedLogonData?.logon,
        },
      }),
    );
  }

  private hasInvalidEntries(): boolean {
    let invalid = false;

    this.entriesForm.controls.forEach((group, index) => {
      const emailControl = group.controls.email;
      const accountNameControl = group.controls.accountName;
      const email = emailControl.value.trim();

      if (!email) {
        if (index === 0) {
          emailControl.setErrors({
            ...(emailControl.errors ?? {}),
            required: true,
          });
          invalid = true;
        }
        return;
      }

      if (emailControl.invalid || accountNameControl.invalid) {
        invalid = true;
      }
    });

    return invalid;
  }

  private _openSuccessModal(results: CloneLogonResult[]): void {
    this._dialog.open(
      UserAddSuccessModalComponent,
      { results },
      { width: "500px" },
    );
  }

  public getNextButtonTitle(): string {
    switch (this.currentStep) {
      case ViewProductsStep.PRODUCTS:
        return "Criar usuário";
      case ViewProductsStep.FORM:
        return "Criar usuários";
      default:
        return "Continuar";
    }
  }

  public isNextButtonDisabled(): boolean {
    return (
      this.currentStep === ViewProductsStep.PRODUCTS && this.isPmeLimitReached()
    );
  }

  public onNextButtonClick(): void {
    switch (this.currentStep) {
      case ViewProductsStep.PRODUCTS:
        this.continueToForm();
        break;
      case ViewProductsStep.FORM:
        this.addUser();
        break;
    }
  }
}
